import * as Sentry from '@sentry/react';
import {MessageHandler} from '@sumsub/websdk';
import SumSubWebSdk from '@sumsub/websdk-react';
import React, {useEffect, useRef} from 'react';
import {GenericErrorMsg} from '~/entities/dialogs';
import {useAppDispatch} from '~/shared/store';

const SubmittedSumSubMsgTypes = ['idCheck.onApplicantSubmitted', 'onApplicantResubmitted'];
const StatusChangedSumSubMsgType = 'idCheck.onApplicantStatusChanged' as const;

// Vile hack because SumSub types are shit
declare const __MessageHandler: MessageHandler | undefined;
type SumSubMsgType = Parameters<typeof __MessageHandler & {}>[0];
type SumSubMsg = {
    [K in SumSubMsgType]: {type: K; payload: Parameters<typeof __MessageHandler<K> & {}>[1]};
}[SumSubMsgType];
type SumSubMsgHandler = (message: SumSubMsg) => void;

export function KYCForm({
    token,
    getNewToken,
    onFinish,
}: {
    token: string;
    getNewToken: () => Promise<string>;
    onFinish: () => void;
}) {
    const dispatch = useAppDispatch();
    const timeout = useRef<NodeJS.Timeout>();
    useEffect(() => () => clearTimeout(timeout.current));

    const handlePageLeave = (event?: BeforeUnloadEvent) => {
        // don't send an alert if no submission attempt was made
        if (!batchedMessagesRef.current.some(t => SubmittedSumSubMsgTypes.includes(t))) {
            return;
        }
        // if submitted, report the missing status as a potential misconfiguration
        if (!batchedMessagesRef.current.includes(StatusChangedSumSubMsgType)) {
            Sentry.captureException(new Error('None of the expected message types from SumSub SDK were received'), {
                level: 'error',
                extra: {
                    Expected: StatusChangedSumSubMsgType,
                    Received: batchedMessagesRef.current,
                },
            });
            Sentry.flush();
        }
        event?.preventDefault();
    };

    const batchedMessagesRef = useRef<string[]>([]);
    useEffect(() => {
        window.addEventListener('beforeunload', handlePageLeave);
        return () => {
            window.removeEventListener('beforeunload', handlePageLeave);
            handlePageLeave();
        };
    }, []);

    const onMessage: SumSubMsgHandler = msg => {
        batchedMessagesRef.current.push(msg.type);
        if (msg.type !== StatusChangedSumSubMsgType) {
            return;
        }
        const payload = msg.payload as typeof msg.payload & {
            reviewStatus: 'completed' | 'pending';
            reviewResult: {
                reviewAnswer: 'GREEN' | 'RED';
                reviewRejectType?: 'RETRY' | 'FINAL';
            };
        };
        if (payload.reviewStatus !== 'completed') {
            return;
        }
        switch (payload.reviewResult.reviewAnswer) {
            case 'RED': {
                if (payload.reviewResult.reviewRejectType === 'FINAL') {
                    timeout.current = setTimeout(onFinish, 8000);
                }
                break;
            }
            case 'GREEN': {
                timeout.current = setTimeout(onFinish, 2000);
                break;
            }
        }
    };

    return (
        <SumSubWebSdk
            accessToken={token}
            expirationHandler={getNewToken}
            config={{
                theme: 'light',
                lang: 'en',
            }}
            options={{
                adaptIframeHeight: false,
                addViewportTag: true,
            }}
            className={'flex flex-1'}
            style={{height: '100%'}}
            onMessage={(type, payload) => onMessage({type, payload} as SumSubMsg)}
            onError={error => {
                dispatch(
                    GenericErrorMsg.open({
                        details: error.reason,
                    })
                );
            }}
        />
    );
}
